import React from "react";
import { motion } from "framer-motion";
import { ArrowLeft, BookOpen, Target, Star, Users, DollarSign, MessageCircle, Award, Clock, Shield } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";

export const MentorGuide = () => {
  const navigate = useNavigate();

  const sections = [
    {
      id: "welcome",
      title: "1. Bem-vindo à Arena Pro Player",
      icon: <BookOpen className="h-6 w-6 text-[#00B6F0]" />,
      content: `Parabéns por se tornar um mentor na Arena Pro Player! 🎉

Como mentor, você tem a oportunidade única de compartilhar suas habilidades e conhecimentos com jogadores apaixonados que desejam melhorar sua performance nos games.

Este guia irá ajudá-lo a:
• Configurar seu perfil de forma atrativa
• Entender como funciona o sistema de mentorias
• Maximizar seus ganhos e avaliações
• Construir uma reputação sólida na plataforma

Seja bem-vindo à nossa comunidade de pro players! 🎮`
    },
    {
      id: "profile-setup",
      title: "2. Configurando seu Perfil",
      icon: <Star className="h-6 w-6 text-[#00B6F0]" />,
      content: `Um perfil bem configurado é fundamental para atrair alunos:

📸 Foto de Perfil:
• Use uma foto profissional e de qualidade
• Mostre-se confiante e acessível
• Evite imagens de jogos ou genéricas

🎮 Especialização:
• Liste seus jogos principais com ranks específicos
• Inclua conquistas e títulos importantes
• Seja específico sobre suas especialidades (ex: "Coach de rotação em League of Legends")

📝 Biografia:
• Conte sua história como jogador
• Destaque experiências relevantes (competições, times, coaching)
• Mantenha um tom profissional mas acessível
• Use entre 150-300 palavras

💰 Preços:
• Pesquise valores de outros mentores similares
• Comece com preços competitivos para ganhar avaliações
• Ofereça diferentes pacotes (sessão única, pacotes semanais)`
    },
    {
      id: "mentoring-best-practices",
      title: "3. Melhores Práticas de Mentoria",
      icon: <Target className="h-6 w-6 text-[#00B6F0]" />,
      content: `Para se destacar como mentor, siga estas diretrizes:

🎯 Antes da Sessão:
• Converse com o aluno sobre objetivos específicos
• Peça replays ou informações sobre o nível atual
• Prepare materiais relevantes (builds, guias, exercícios)
• Teste sua conexão e equipamentos

🏆 Durante a Mentoria:
• Seja pontual e profissional
• Explique conceitos de forma clara e progressiva
• Use exemplos práticos e demonstrações
• Incentive perguntas e participe ativamente
• Mantenha o foco nos objetivos definidos

📈 Feedback e Evolução:
• Dê feedback construtivo e específico
• Estabeleça metas de curto e longo prazo
• Sugira exercícios para praticar entre sessões
• Acompanhe o progresso em sessões subsequentes

💡 Dicas Extras:
• Seja paciente - cada aluno tem seu ritmo
• Adapte sua metodologia ao estilo de aprendizado
• Mantenha-se atualizado com mudanças do jogo`
    },
    {
      id: "communication",
      title: "4. Comunicação Efetiva",
      icon: <MessageCircle className="h-6 w-6 text-[#00B6F0]" />,
      content: `A comunicação é chave para o sucesso na mentoria:

💬 Antes da Mentoria:
• Responda mensagens em até 24 horas
• Confirme horários com antecedência
• Esclareça dúvidas sobre objetivos e expectativas
• Seja claro sobre o que será abordado na sessão

🎤 Durante a Sessão:
• Use linguagem clara e evite jargões excessivos
• Faça pausas para verificar compreensão
• Incentive o aluno a fazer perguntas
• Mantenha um ambiente positivo e encorajador

📋 Pós-Mentoria:
• Envie um resumo dos pontos principais
• Indique exercícios ou conteúdos para estudar
• Agende próximas sessões se aplicável
• Peça feedback sobre a experiência

🤝 Profissionalismo:
• Mantenha sempre uma atitude respeitosa
• Seja pontual e cumpra compromissos
• Trate cada aluno com igualdade
• Mantenha confidencialidade quando necessário`
    },
    {
      id: "earnings-payments",
      title: "5. Ganhos e Pagamentos",
      icon: <DollarSign className="h-6 w-6 text-[#00B6F0]" />,
      content: `Entenda como funcionam os pagamentos na plataforma:

💰 Estrutura de Ganhos:
• A Arena Pro Player retém uma taxa de serviço de 15%
• Você recebe 85% do valor pago pelo aluno
• Pagamentos são processados semanalmente
• Métodos: PIX, transferência bancária

📊 Estratégias para Maximizar Ganhos:
• Mantenha alta qualidade para conseguir avaliações 5 estrelas
• Ofereça pacotes com desconto para fidelizar alunos
• Seja ativo e responsivo para aparecer melhor nos resultados
• Desenvolva especializações em nichos específicos

🧾 Questões Fiscais:
• Você é responsável por declarar sua renda
• Mantenha registros organizados das suas mentorias
• A plataforma fornece relatórios mensais
• Considere orientação de um contador para valores altos

📈 Acompanhamento de Performance:
• Use o dashboard para monitorar ganhos
• Analise avaliações para identificar pontos de melhoria
• Acompanhe sua classificação na plataforma`
    },
    {
      id: "building-reputation",
      title: "6. Construindo sua Reputação",
      icon: <Award className="h-6 w-6 text-[#00B6F0]" />,
      content: `Sua reputação é seu maior ativo na plataforma:

⭐ Sistema de Avaliações:
• Alunos avaliam de 1 a 5 estrelas após cada sessão
• Comentários são públicos e impactam sua visibilidade
• Meta: manter média acima de 4.8 estrelas
• Responda a avaliações de forma profissional

🏆 Como Conseguir Excelentes Avaliações:
• Supere expectativas sempre que possível
• Seja proativo em identificar necessidades do aluno
• Demonstre resultado tangível na melhoria
• Mantenha seguimento entre sessões

📈 Crescimento na Plataforma:
• Mentores bem avaliados aparecem primeiro nas buscas
• Plataforma pode destacar mentores exemplares
• Oportunidades especiais para top performers
• Programa de referência para indicar outros mentores

🔄 Lidando com Feedback Negativo:
• Veja críticas como oportunidades de melhoria
• Responda de forma construtiva e profissional
• Ajuste métodos baseado em feedback recorrente
• Procure nossa equipe se precisar de suporte`
    },
    {
      id: "time-management",
      title: "7. Gestão de Tempo",
      icon: <Clock className="h-6 w-6 text-[#00B6F0]" />,
      content: `Organize seu tempo para máxima eficiência:

📅 Planejamento de Agenda:
• Use o sistema de agendamento da plataforma
• Defina horários fixos de disponibilidade
• Reserve tempo para preparação entre sessões
• Mantenha horários atualizados

⏰ Duração das Sessões:
• Sessões padrão: 1 ou 2 horas
• Respeite rigorosamente os horários
• Comece e termine pontualmente
• Use os últimos 10 minutos para resumo e próximos passos

🔄 Balanceamento:
• Evite sobrecarga - qualidade > quantidade
• Mantenha tempo para seu próprio desenvolvimento
• Programe pausas entre sessões longas
• Reserve tempo para estudar atualizações do jogo

📊 Produtividade:
• Prepare templates para feedbacks comuns
• Organize materiais de ensino por tópicos
• Use ferramentas de gravação quando apropriado
• Mantenha registros do progresso dos alunos`
    },
    {
      id: "platform-features",
      title: "8. Recursos da Plataforma",
      icon: <Users className="h-6 w-6 text-[#00B6F0]" />,
      content: `Aproveite ao máximo todas as funcionalidades:

🎮 Ferramentas de Mentoria:
• Sistema de agendamento integrado
• Chat e videochamada nativos
• Compartilhamento de tela para demonstrações
• Gravação de sessões (com consentimento)

📊 Dashboard do Mentor:
• Estatísticas de performance em tempo real
• Histórico de ganhos e pagamentos
• Calendário de sessões agendadas
• Feedback e avaliações recebidas

💬 Comunicação:
• Sistema de mensagens diretas com alunos
• Notificações push para não perder nada
• Chat de suporte 24/7 com nossa equipe
• Fórum exclusivo de mentores

🛡️ Segurança e Suporte:
• Sistema de disputas para resolver conflitos
• Verificação de identidade de todos os usuários
• Políticas claras de conduta
• Suporte técnico especializado`
    },
    {
      id: "community-conduct",
      title: "9. Conduta da Comunidade",
      icon: <Shield className="h-6 w-6 text-[#00B6F0]" />,
      content: `Como mentor, você é um exemplo na nossa comunidade:

✅ Comportamentos Esperados:
• Trate todos com respeito e profissionalismo
• Mantenha linguagem adequada em todas as interações
• Seja inclusivo e acolhedor com todos os tipos de jogadores
• Compartilhe conhecimento de forma generosa

❌ Condutas Proibidas:
• Discriminação por gênero, idade, origem, habilidade
• Assédio ou comportamento inadequado
• Tentativas de contato fora da plataforma inicialmente
• Compartilhamento de conteúdo impróprio

🚨 Sistema de Relatórios:
• Alunos podem reportar comportamentos inadequados
• Investigamos todas as denúncias seriamente
• Ações podem incluir advertência, suspensão ou banimento
• Processo justo com direito de defesa

🤝 Construindo uma Comunidade Positiva:
• Seja mentor não apenas no jogo, mas como pessoa
• Incentive fair play e bom comportamento
• Apoie outros mentores quando apropriado
• Contribua para um ambiente de aprendizado saudável`
    },
    {
      id: "support-resources",
      title: "10. Suporte e Recursos",
      icon: <Users className="h-6 w-6 text-[#00B6F0]" />,
      content: `Estamos aqui para apoiar seu sucesso:

📞 Canais de Suporte:
• WhatsApp: (11) 99642-5204
• E-mail: mentores@arenaproplayer.com
• Chat ao vivo na plataforma
• Horário: Segunda a Sexta, 9h às 22h

📚 Recursos de Desenvolvimento:
• Webinars mensais sobre técnicas de coaching
• Biblioteca de materiais educacionais
• Comunidade privada de mentores no Discord
• Templates e guias para diferentes jogos

🆘 Quando Procurar Ajuda:
• Problemas técnicos com a plataforma
• Conflitos com alunos
• Dúvidas sobre pagamentos
• Sugestões de melhorias

🏆 Programa de Mentores Élite:
• Reconhecimento especial para top performers
• Oportunidades de eventos exclusivos
• Participação em desenvolvimento de novos recursos
• Bonificações especiais por performance`
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0c1117] via-[#0f141c] to-[#0c1117]">
      {/* Header */}
      <div className="bg-black/50 backdrop-blur-sm border-b border-gray-800">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-4 text-gray-400 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Guia do <span className="text-[#00B6F0]">Mentor</span>
            </h1>
            <p className="text-gray-300 text-lg">
              Seu manual completo para o sucesso na Arena Pro Player
            </p>
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="space-y-8"
        >
          {sections.map((section, index) => (
            <motion.div
              key={section.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 * index }}
              className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6 border border-gray-800 hover:border-[#00B6F0]/30 transition-colors"
            >
              <div className="flex items-center gap-3 mb-4">
                {section.icon}
                <h2 className="text-xl md:text-2xl font-bold text-white">
                  {section.title}
                </h2>
              </div>
              
              <div className="text-gray-300 leading-relaxed space-y-4">
                {section.content.split('\n\n').map((paragraph, idx) => (
                  <p key={idx} className="whitespace-pre-line">
                    {paragraph}
                  </p>
                ))}
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Footer CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.8 }}
          className="mt-12 p-6 bg-gradient-to-r from-[#00B6F0]/10 to-[#8B5CF6]/10 rounded-xl border border-[#00B6F0]/30"
        >
          <h3 className="text-xl font-bold text-white mb-3">
            🚀 Pronto para Começar?
          </h3>
          <p className="text-gray-300 leading-relaxed mb-4">
            Agora que você conhece todos os aspectos importantes, está pronto para oferecer 
            mentorias excepcionais e construir uma carreira de sucesso na Arena Pro Player!
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button 
              onClick={() => navigate("/mentor/dashboard")}
              className="bg-gradient-to-r from-[#00B6F0] to-[#0077B6] text-black font-bold"
            >
              Ir para Dashboard
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate("/cadastro")}
              className="border-[#00B6F0] text-[#00B6F0] hover:bg-[#00B6F0]/10"
            >
              Começar como Mentor
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}; 